import { useContext, useEffect, useState } from 'react';
import { Box, Button, Stack, SvgIcon, useTheme, useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Plus } from '../../assets';
import { UserContext } from '../../components/shared/useUser';
import { useLocation, useNavigate } from 'react-router-dom';
import PageArea from '../../components/shared/PageArea';
import PageTitle from '../../components/shared/PageTitle';
import CategoriesTab from './components/CategoriesTab';
import useLogError from '../../hooks/useLogError';
import { client } from '../../app/services/api/orderManagementClient';

const CategoriesPage = () => {
  const { t } = useTranslation('categories');
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { logError } = useLogError();

  const { hasPermissionTo, selectedStore } = useContext(UserContext);
  const [hasPermissionToCreateCategory, setHasPermissionToCreateCategory] = useState<boolean>(false);
  const isMobile = useMediaQuery(`(max-width:${theme.breakpoints.values.md}px)`);
  
  const [currentFilterInstance, setCurrentFilterInstance] = useState<string>('');
  const [currentFilterSearchTerm, setCurrentFilterSearchTerm] = useState<string>('');
  const [isInitializingFilters, setIsInitializingFilters] = useState<boolean>(false);

  useEffect(() => {
    setHasPermissionToCreateCategory(hasPermissionTo(['CategoryCreate', 'ConfigurationAdmin']));

    const searchParams = new URLSearchParams(location.search);
    const companyIdParam = searchParams.get('companyId');
    const searchTermParam = searchParams.get('searchTerm');

    setCurrentFilterInstance(companyIdParam || '');
    setCurrentFilterSearchTerm(searchTermParam || '');

  }, [hasPermissionTo, location.search]);

  const handleCreateNew = () => {
    navigate('/categories/create');
  };

  const canViewCategories = hasPermissionTo(['CategoryView', 'ConfigurationAdmin']);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (!searchParams.get('companyId')) {
      loadStoreDefaultFilters();
    }
  }, [selectedStore]);

  const loadStoreDefaultFilters = async () => {
    if (!selectedStore?.storeNumber || !selectedStore?.franchiseUUID) return;
    try {
      const companyResult = await client.getCompanyIdByStoreNumber(selectedStore.storeNumber);
      if (!companyResult.success || !companyResult.data) return;

      const instanceId = companyResult.data.toString();
      setCurrentFilterInstance(instanceId);
      const params = new URLSearchParams();
      params.set('companyId', instanceId);
      navigate(`?${params.toString()}`, { replace: true });

    } catch (error) {
      logError('Failed to load store default filters');
    }
  };

  const handleFilterChange = (instance: string, searchTerm?: string) => {
    setCurrentFilterInstance(instance);
    const newSearchTerm = searchTerm !== undefined ? searchTerm : currentFilterSearchTerm;
    setCurrentFilterSearchTerm(newSearchTerm);

    const params = new URLSearchParams();
    if (instance) params.set('companyId', instance);
    if (newSearchTerm) params.set('searchTerm', newSearchTerm);
    navigate(`?${params.toString()}`, { replace: true });
  };

  if (!canViewCategories) {
    return null;
  }

  return (
    <PageArea>
      <Stack
        width='100%'
        spacing={2}
        direction={isMobile ? 'column' : 'row'}
        sx={{
          justifyContent: 'space-between',
        }}
      >
        <Box width={isMobile ? '100%' : 'auto'}>
          <PageTitle>{t('pageTitle')}</PageTitle>
        </Box>

        {hasPermissionToCreateCategory && (
          <Box
            width={isMobile ? '100%' : 'auto'}
            sx={{
              display: 'flex',
              justifyContent: { xs: 'flex-start', md: 'flex-end' },
            }}
          >
            <Button
              variant="primary"
              size="lg"
              startIcon={<SvgIcon><Plus fill='white' /></SvgIcon>}
              onClick={handleCreateNew}
              sx={{
                mb: { xs: 4, md: 0 },
              }}
            >
              {t('createNew')}
            </Button>
          </Box>
        )}
      </Stack>
      
      <CategoriesTab 
        onCreateNew={handleCreateNew} 
        onFilterChange={handleFilterChange}
        initialInstance={currentFilterInstance}
        initialSearchTerm={currentFilterSearchTerm}
      />
    </PageArea>
  );
};

export default CategoriesPage;
